import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Trash2 } from "lucide-react";

interface ItemCardCstProps {
  title: React.ReactNode;
  segments?: React.ReactNode[];
  actions?: React.ReactNode;
  onDelete?: () => void;
}

const ItemCardCst: React.FC<ItemCardCstProps> = ({
  title,
  segments,
  actions,
  onDelete,
}) => {
  const theme = useTheme();

  const visibleSegments = (segments || []).filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        padding: "16px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {visibleSegments.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: 0,
              fontSize: "13px",
              lineHeight: 1.4,
              color: theme.palette.text.primary,
            }}
          >
            {visibleSegments.map((segment, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <Box component="span" sx={{ color: theme.palette.divider }}>
                    |
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {segment}
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>

      {(actions || onDelete) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.7,
            flexShrink: 0,
            "& .MuiIconButton-root": { mr: 0 },
          }}
        >
          {actions}
          {onDelete && (
            <Box
              onClick={onDelete}
              sx={{
                display: "flex",
                cursor: "pointer",
                color: theme.palette.primary.main,
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Trash2 size={20} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ItemCardCst;
